import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import {
  AppShellRootComponent,
  injectShellRouteData,
  type ShellHeader,
  type ShellNavEntry,
} from '@sbe-spinoff-client/app-shell';
import {
  SpinoffActiveOrgContextService,
  SpinoffAuthService,
  SpinoffNotificationBellComponent,
  SpinoffOrgSwitcherComponent,
  SpinoffProfileMenuComponent,
  injectSpinoffBootLoading,
  injectSpinoffLoginCta,
  type OrgSummaryDto,
} from '@sbe-spinoff-client/spinoff-platform';
import { appRoutes } from './app.routes';

interface ShellRouteData extends Record<string, unknown> {
  shellMode: ShellHeader;
  /** true면 <sbe-app-shell> 자체를 건너뛴다(팝업/콜백 라우트) — footer도 없음. */
  shellBare: boolean;
}

@Component({
  imports: [
    AppShellRootComponent,
    SpinoffOrgSwitcherComponent,
    SpinoffProfileMenuComponent,
    SpinoffNotificationBellComponent,
    MatButtonModule,
    RouterLink,
  ],
  selector: 'app-root',
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './app.scss',
})
export class App {
  protected readonly auth = inject(SpinoffAuthService);
  private readonly router = inject(Router);
  protected readonly activeContext = inject(SpinoffActiveOrgContextService);

  /**
   * 셸이 라우트마다 다른 모습을 갖도록 구동하는 라우트 데이터 — `app.routes.ts`의
   * `data: { shellMode, shellBare }` 선언을 읽는다(`libs/client/app-shell`의
   * `injectShellRouteData()`). 헤더는 "항상 보이거나 항상 안 보이거나 둘 중 하나로
   * 통일"하라는 지시(2026-08-12)에 따라 일반 라우트는 전부 `shellMode: 'toolbar'`를
   * 선언한다 — 랜딩/로그인도 예전의 `'none'`을 버리고 헤더를 노출한다(인증 후 전용
   * 슬롯은 `showToolbarChrome()`이 `auth.isAuthenticated()`로 계속 걸러낸다).
   * 팝업/콜백(`shellBare: true`)만 예외다 — 처리 중 즉시 이탈하는 화면이라 셸 자체를
   * 건너뛰며, 이때는 `shellMode` 값 자체가 무의미하다(`app-shell-root.component.html`이
   * `shellBare`가 true면 header를 무조건 `'none'`으로 덮어써서 shellMode를 무시한다).
   *
   * 기본값(`initialValue`)을 `'toolbar'`로 둔다 — `toSignal`의 `initialValue`는 라우터의
   * 최초 내비게이션(`initialNavigation: 'enabledNonBlocking'`, `app.config.ts`에 별도
   * 설정 없음)이 끝나기 **전** `router.routerState.snapshot.root`를 동기적으로 읽어
   * 계산되므로, 콜드 로드 시 실제 라우트가 뭐든 이 기본값이 첫 렌더에 잠깐 노출된다.
   * 이제 선언된 라우트가 전부 `'toolbar'`이므로(팝업/콜백은 값 자체가 무시됨) 기본값도
   * `'toolbar'`로 맞추면 어떤 라우트로 콜드 로드하든 깜빡임이 없다.
   */
  protected readonly shellRouteData = injectShellRouteData<ShellRouteData>({
    shellMode: 'toolbar',
    shellBare: false,
  });

  /**
   * toolbar 슬롯(조직 스위처·알림 종·프로필 메뉴)을 렌더할지 — `auth.isAuthenticated()`만
   * 보면 이미 로그인한 사용자가 랜딩('/')에 북마크 등으로 직접 들어왔을 때도 이 컴포넌트들이
   * 인스턴스화된다(SpinoffNotificationBellComponent는 생성자에서 곧바로 비인증 컨텍스트에서
   * API를 호출). shellMode가 'toolbar'일 때만 인증 여부를 본다 — `shellBare`인 팝업/콜백에서는
   * 셸 자체가 이 슬롯들을 그리지 않으므로 애초에 필요 없다.
   */
  protected readonly showToolbarChrome = computed(
    () => this.shellRouteData().shellMode === 'toolbar' && this.auth.isAuthenticated(),
  );

  /** 미로그인 상태에서 헤더 우측 프로필 메뉴 자리에 대신 노출하는 로그인 버튼 게이팅 — showToolbarChrome()과 같은 shellMode 조건, 인증 여부만 반대. */
  protected readonly showLoginButton = computed(
    () => this.shellRouteData().shellMode === 'toolbar' && !this.auth.isAuthenticated(),
  );

  /**
   * guard로 보호된 경로(대시보드 등)로 콜드 진입/새로고침한 경우에만 콘텐츠 영역에
   * 부팅 오버레이를 띄운다 — spinoffAuthGuard가 auth.init()을 기다리는 동안
   * router-outlet이 빈 화면으로 보이는 것을 막기 위함. 공개 경로('/', '/login' 등)는
   * 각 컴포넌트가 자체 로딩 상태를 표시하므로 여기서는 막지 않는다.
   * `@sbe-spinoff-client/spinoff-platform`의 `injectSpinoffBootLoading()`으로
   * 이관했다(2026-08-13) — 이 앱의 원본 구현(2026-08-11, `PUBLIC_TOP_SEGMENTS`/
   * `isProtectedEntry`/`bootGate.ensureStarted()` 수동 호출)이 그 훅의 canonical
   * 소스였다.
   */
  protected readonly showBootLoading = injectSpinoffBootLoading(appRoutes);

  protected readonly loginCta = injectSpinoffLoginCta();

  /**
   * org 컨텍스트(`/orgs/:orgId/...`) 안에서만 채워지는 전역 사이드바 항목 — org 밖(대시보드
   * 등)에서는 `[]`이라 `hasSidenav()`가 false가 되어 셸이 사이드바 자체를 렌더하지 않는다.
   * `sbe-schedule/web`의 동일 패턴(`activeContext.orgId()` 기반 computed) 그대로 재사용.
   *
   * 예전엔(2026-08-10~08-28) 같은 자리에 이 계산이 있었다가, org 라우트 트리 전용 로컬
   * 2차 사이드바(`OrgSettingsShellComponent`/`SpinoffOrgSettingsLayoutComponent`)와
   * 동시에 뜨는 이중 사이드바 버그 때문에 삭제되고 로컬 셸로만 수렴했었다(2026-08-28
   * 플랜, `libs/client/spinoff-platform/plans/archive/2026-08-28-org-settings-layout-shell.md`
   * 참고). 이번엔 `@sbe-spinoff-client/app-shell` 자체에 heading/섹션 그룹(`ShellNavSection`)
   * 지원이 생겨(2026-08-29) 로컬 셸이 하던 걸 이 전역 사이드바 하나로 대체할 수 있게 됐다 —
   * `OrgSettingsShellComponent`/`SpinoffOrgSettingsLayoutComponent`는 이 커밋에서 삭제한다.
   */
  protected readonly navItems = computed<readonly ShellNavEntry[]>(() => {
    const orgId = this.activeContext.orgId();
    if (!orgId) return [];
    // segment에 '/'가 있으면(예: 'settings/templates') routerLink 배열의 한 원소로 그대로
    // 넣지 않는다 — RouterLink는 배열 원소 하나를 "리터럴 세그먼트 하나"로 취급해 안의
    // '/'를 %2F로 인코딩한다(구 OrgSettingsShellComponent.sections()의 동일 주석 참고).
    const path = (segment: string): readonly string[] => ['/orgs', orgId, ...segment.split('/')];
    const notImplementedYet = ['/orgs', orgId] as const;
    return [
      {
        items: [
          { label: '발송', routerLink: path('send') },
          { label: '이력', routerLink: path('history') },
          { label: '크레딧 충전 및 관리', routerLink: path('credits') },
          { label: '템플릿', routerLink: path('settings/templates') },
          { label: '설정', routerLink: path('settings') },
        ],
      },
      {
        heading: '단체 설정',
        items: [
          { label: '구성원', routerLink: ['/orgs', orgId], exact: true },
          // 학급구조에서 학급을 고르면 그 학급의 학생 명단(`teams/:teamId/members`)으로 들어간다.
          { label: '학급·학생 명단', routerLink: path('teams') },
          { label: '멤버 분류', routerLink: path('labels') },
          { label: '권한 설정', routerLink: notImplementedYet, disabled: true },
          { label: '감사 로그', routerLink: path('audit-log') },
        ],
      },
    ];
  });

  /**
   * 인프라 장애 전체 화면에서 "다시 시도" 클릭 시 호출. 개별 요청을 재현하는
   * 대신 새로고침한다 — 부팅 시 refresh 실패든 런타임 중 임의 API 실패든
   * 동일한 진입점(auth.init() 재실행)으로 되돌리는 가장 단순하고 확실한 방법이다.
   */
  protected retry(): void {
    window.location.reload();
  }

  protected async onOrgSelected(org: OrgSummaryDto): Promise<void> {
    await this.router.navigateByUrl(`/orgs/${org.id}`);
  }

  protected async onInvitationsRequested(): Promise<void> {
    await this.router.navigateByUrl('/my-org-invitations');
  }

  protected async logout(): Promise<void> {
    await this.auth.signOut();
    await this.router.navigateByUrl('/');
  }
}
